import { TranslateService } from '@ngx-translate/core';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, Validators, ValidationErrors, UntypedFormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { MetricValidators } from 'src/app/core/validators/metric.validators';
import { MetricEdit } from '../model/metricEdit';
import { ReportListService } from 'src/app/core/service/grid-data/grid-data-report.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';

@Component({
  selector: 'app-metric-edit',
  templateUrl: './metric-edit.component.html',
  styleUrls: ['./metric-edit.component.scss']
})
export class MetricEditComponent implements OnInit
{
  @ViewChildren(TooltipDirective) public tooltipDir: QueryList<TooltipDirective>;

  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<MetricEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public metricsLookup: any = [];

  private metricEdit: MetricEdit;
  private textNumerator = '';
  private textDenominator = '';
  private textNumeratorProgress = '';
  private textDenominatorGoal = '';

  constructor(
    private reportListService: ReportListService,
    private fb: UntypedFormBuilder,
    private translate: TranslateService,
    private metricValidators: MetricValidators,
    private messageHandlerService: MessageHandlerService
  )
  { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get description(): UntypedFormControl
  {
    return this.editForm.get('description');
  }

  public get numerator(): UntypedFormControl
  {
    return this.editForm.get('numerator');
  }

  public get numeratorDescription(): UntypedFormControl
  {
    return this.editForm.get('numeratorDescription');
  }

  public get denominator(): UntypedFormControl
  {
    return this.editForm.get('denominator');
  }

  public get denominatorDescription(): UntypedFormControl
  {
    return this.editForm.get('denominatorDescription');
  }

  public get metricType(): UntypedFormControl
  {
    return this.editForm.get('metricType');
  }

  public get metricRateMode(): UntypedFormControl
  {
    return this.editForm.get('metricRateMode');
  }

  @Input() public set model(metric: MetricEdit)
  {
    this.initModel(metric);
  }

  public ngOnInit(): void
  {
    this.translate.get(['NUMERATOR', 'DENOMINATOR', 'DEFAULT-NUMERATOR-PROGRESS', 'DEFAULT-DENOMINATOR-GOAL']).subscribe({
      next: (res) =>
      {
        this.textNumerator = res.NUMERATOR;
        this.textDenominator = res.DENOMINATOR;
        this.textNumeratorProgress = res['DEFAULT-NUMERATOR-PROGRESS'];
        this.textDenominatorGoal = res['DEFAULT-DENOMINATOR-GOAL'];
      }
    });
  }

  public onSave(e: Event): void
  {
    e.preventDefault();
    const objectToPersist = this.editForm.value;

    if (objectToPersist.numerator && objectToPersist.denominator && objectToPersist.numerator.id === objectToPersist.denominator.id)
    {
      this.messageHandlerService.displayError('NUMERATOR-AND-DENOMINATOR-MUST-BE-DIFFERENT');
      return;
    }

    if (this.metricType.value === 'COUNT_BASED')
    {
      objectToPersist.metricRateMode = null;
    }

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  public showTooltip(e: ElementRef, tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.toggle(e, true); // Show the tooltip
    }
  }

  public hideTooltip(tooltip: ElementRef): void
  {
    const tooltipDirective = this.tooltipDir.toArray().find(t => t.tooltipWrapper.nativeElement === tooltip);
    if (tooltipDirective)
    {
      tooltipDirective.hide(); // Show the tooltip
    }
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(metricEdit: MetricEdit)
  {
    if (metricEdit === undefined)
    {
      return;
    }

    // Load metrics
    this.loadMetrics();

    // Set the current metric id used for name async validators
    const previousMetricName = this.isNew ? '' : metricEdit.name;

    // Set form
    this.editForm = this.fb.group({
      id: [metricEdit.id],
      version: [metricEdit.version],
      name: [
        { value: '', disabled: false },
        {
          validators: [Validators.required],
          asyncValidators: (): Observable<ValidationErrors | null> =>
            this.metricValidators.validateMetricNameUnique(this.editForm.get('name'), previousMetricName)
        }
      ],
      metricType: [
        { value: metricEdit.metricType, disabled: false }, Validators.required
      ],
      metricRateMode: [
        { value: metricEdit.metricRateMode, disabled: false }, metricEdit.metricType === 'RATE_BASED' ? Validators.required : null
      ],
      description: [
        { value: metricEdit.description, disabled: false }, Validators.required
      ],
      numerator: [{ value: metricEdit.numerator, disabled: false }],
      numeratorDescription: [
        { value: metricEdit.numeratorDescription, disabled: false }, Validators.required
      ],
      denominator: [{ value: metricEdit.denominator, disabled: false }],
      denominatorDescription: [
        { value: metricEdit.denominatorDescription, disabled: false }, Validators.required
      ],
      active: [{ value: metricEdit.active, disabled: false }, Validators.required]
    });

    this.editForm.get('metricType').valueChanges.subscribe({
      next: () =>
      {
        this.setRateModeState();
        this.setNumeratorAndDenominatorDefault();
      }
    });

    this.editForm.reset(metricEdit);
    this.metricEdit = metricEdit;
    this.editFormActive = metricEdit !== undefined;
  }

  private setRateModeState(): void
  {
    const rateModeValidator = [];
    if (this.metricType.value === 'RATE_BASED')
    {
      rateModeValidator.push(Validators.required);
    }
    this.metricRateMode.setValidators(rateModeValidator);
    this.metricRateMode.updateValueAndValidity();
  }

  private setNumeratorAndDenominatorDefault(): void
  {
    if (this.metricType.value === 'RATE_BASED')
    {
      this.numeratorDescription.setValue(this.textNumerator);
      this.denominatorDescription.setValue(this.textDenominator);
    }
    else if (this.metricType.value === 'COUNT_BASED')
    {
      this.numeratorDescription.setValue(this.textNumeratorProgress);
      this.denominatorDescription.setValue(this.textDenominatorGoal);
    }
  }

  private loadMetrics(): void
  {
    const metricsState = {
      filter: {
        filters: [
          { field: 'reportType', operator: 'eq', value: 'METRIC' },
        ], logic: 'and'
      },
      group: [], skip: 0, sort: [], take: 99999
    };
    this.reportListService.queryByState(metricsState, (metrics: any) =>
    {
      this.metricsLookup = metrics.data;
    }, '&loadAllMetrics=true');
  }
}
